/**
 * usePublicProfileStatistics Hook
 *
 * Custom hook for fetching public profile statistics (Active + Upcoming rituals only)
 * Used for profile/other profile pages
 * Uses React Query for data fetching with caching and automatic refetching
 */

import { useQuery } from "@tanstack/react-query"
import { getPublicProfileStatistics } from "../services/public-profile-statistics.service"

export interface UsePublicProfileStatisticsOptions {
  enabled?: boolean
}

/**
 * Hook to fetch public profile statistics
 * @param userId - The user ID to fetch statistics for
 * @param options - Optional configuration
 * @returns React Query result with public statistics data
 */
export function usePublicProfileStatistics(
  userId: string,
  options?: UsePublicProfileStatisticsOptions,
) {
  return useQuery({
    queryKey: ["publicProfileStatistics", userId],
    queryFn: () => getPublicProfileStatistics(userId),
    enabled: !!userId && options?.enabled !== false, // Only run if userId exists
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - cache time (formerly cacheTime)
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Refetch when component mounts
  })
}
